import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X, CheckCircle2, AlertTriangle, Info, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../atoms/button';

const notificationVariants = cva(
  'relative flex w-full items-center gap-3 rounded-10 border p-4 text-sm shadow-md',
  {
    variants: {
      tone: {
        success: 'border-border-success bg-success-subtle text-foreground-success',
        warning: 'border-border-warning bg-warning-subtle text-foreground-warning',
        error: 'border-border-danger bg-danger-subtle text-foreground-danger',
        info: 'border-border-info bg-info-subtle text-foreground-info',
        neutral: 'border-border bg-surface-raised text-foreground',
      },
      variant: {
        toast: 'max-w-sm',
        inline: 'shadow-none',
        stacked: 'max-w-sm',
      },
    },
    defaultVariants: {
      tone: 'info',
      variant: 'inline',
    },
  },
);

const toneIcon = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
  neutral: Info,
} as const;

export interface NotificationProps extends VariantProps<typeof notificationVariants> {
  className?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: React.ReactNode;
  progress?: number;
  actionable?: boolean;
  children?: React.ReactNode;
}

const Notification = React.forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      className,
      tone = 'info',
      variant,
      title,
      description,
      dismissible,
      onDismiss,
      action,
      progress,
      actionable,
      children,
    },
    ref,
  ) => {
    const Icon = toneIcon[tone ?? 'info'];

    return (
      <div
        ref={ref}
        role="status"
        className={cn(notificationVariants({ tone, variant }), className)}
      >
        <Icon className="size-5 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1">
          {title && (
            <div className={cn('font-medium text-foreground', variant === 'toast' && 'truncate')}>
              {title}
            </div>
          )}
          {description && (
            <div className={cn('text-muted-foreground', variant === 'toast' && 'truncate')}>
              {description}
            </div>
          )}
          {children}
          {typeof progress === 'number' && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-overlay">
              <div
                className="h-full bg-brand transition-all"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          )}
          {(actionable || action) && (
            <div className="flex gap-2 pt-2">
              {action ?? (
                <Button size="sm" variant="default" onClick={onDismiss}>
                  Dismiss
                </Button>
              )}
            </div>
          )}
        </div>
        {dismissible && (
          <button
            type="button"
            className="cursor-pointer text-icon-tertiary hover:text-icon-primary"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  },
);
Notification.displayName = 'Notification';

export interface NotificationStackProps {
  className?: string;
  children?: React.ReactNode;
}

const NotificationStack = React.forwardRef<HTMLDivElement, NotificationStackProps>(
  ({ className, children }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-2', className)}>
      {children}
    </div>
  ),
);
NotificationStack.displayName = 'NotificationStack';

export { Notification, NotificationStack, notificationVariants };
